import { Typography } from '@execonline-inc/execonline-ui';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { observer } from 'mobx-react';
import { useContext } from 'react';
import CarouselDisplayTypeChoice from '../../../../../../../Cutesies/CarouselDisplayTypeChoice';
import GridDisplayTypeChoice from '../../../../../../../Cutesies/GridDisplayTypeChoice';
import ListDisplayTypeChoice from '../../../../../../../Cutesies/ListDisplayTypeChoice';
import { T, TranslationsContext, translation } from '../../../../../../../../Translations';
import { AnchoredModal } from '../../../components/AnchoredModal';
import DisplayTypeOption from './DisplayTypeOption';
import { DisplayTypeDropdownProps } from './Types';

export { default as EyeButton } from './EyeButton';
export type { DisplayType, EyeButtonProps } from './Types';

function DisplayTypeDropdown({
  currentDisplayType,
  currentDisplayButtonLabel,
  onDisplayTypeChange,
  onDisplayButtonLabelChange,
  children,
}: DisplayTypeDropdownProps) {
  const ts = useContext(TranslationsContext);
  return (
    <AnchoredModal
      placement="bottom-end"
      closeOnContentClick={false}
      showArrow={false}
      aria-labelledby="display-type-modal-title"
      children={({ onClose }) => (
        <div className="relative px-5 pt-2 pb-5 max-w-lg">
          <button
            type="button"
            onClick={onClose}
            className="absolute -top-1 -right-1 p-1 text-muted-foreground hover:text-foreground transition-colors"
            aria-label={translation('Close dialog')(ts)}
            data-intercom-target="builder-v2-display-type-modal-close"
          >
            <XMarkIcon className="w-6 h-6" aria-hidden="true" />
          </button>

          <div className="mb-3">
            <Typography
              id="display-type-modal-title"
              variant="h6"
              fontWeight="semibold"
              data-intercom-target="builder-v2-display-type-modal-title"
            >
              <T kind="Change Section Display" />
            </Typography>
            <Typography
              variant="bodyMedium"
              className="text-muted-foreground mt-1"
              data-intercom-target="builder-v2-display-type-modal-subtitle"
            >
              <T kind="Choose how your section displays to participants." />
            </Typography>
          </div>

          <hr className="border-border mb-4" />

          <div className="flex items-center gap-1 mb-3">
            <Typography
              variant="bodySmall"
              color="gray"
              fontWeight="semibold"
              data-intercom-target="builder-v2-section-display-type-label"
            >
              <T kind="Display Style" />
            </Typography>
          </div>

          <div
            className="flex gap-3"
            role="radiogroup"
            aria-label={translation('Section display type')(ts)}
          >
            <DisplayTypeOption
              label={translation('Carousel')(ts)}
              icon={<CarouselDisplayTypeChoice />}
              isSelected={currentDisplayType === 'carousel'}
              onSelect={() => onDisplayTypeChange('carousel')}
              intercomTarget="builder-v2-display-type-carousel"
            />
            <DisplayTypeOption
              label={translation('Grid')(ts)}
              icon={<GridDisplayTypeChoice />}
              isSelected={currentDisplayType === 'grid'}
              onSelect={() => onDisplayTypeChange('grid')}
              intercomTarget="builder-v2-display-type-grid"
            />
            <DisplayTypeOption
              label={translation('List')(ts)}
              icon={<ListDisplayTypeChoice />}
              isSelected={currentDisplayType === 'list'}
              onSelect={() => onDisplayTypeChange('list')}
              intercomTarget="builder-v2-display-type-list"
            />
          </div>

          <hr className="border-border my-4" />

          <div className="flex items-center gap-1 mb-2">
            <Typography
              variant="bodySmall"
              color="gray"
              fontWeight="semibold"
              data-intercom-target="builder-v2-section-button-label-label"
            >
              <T kind="Button Label" />
            </Typography>
          </div>

          <Typography
            variant="caption"
            className="text-muted-foreground mb-3 block"
            data-intercom-target="builder-v2-section-button-label-description"
          >
            <T kind="Optionally hide your section by default; participants click a button to view the section. If blank the section will display directly." />
          </Typography>

          <input
            type="text"
            placeholder={translation('e.g., View Programs')(ts)}
            value={currentDisplayButtonLabel.getOrElseValue('')}
            onChange={(e) => onDisplayButtonLabelChange(e.target.value)}
            className="w-full px-3 py-2 border border-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-ring focus:border-primary"
            data-intercom-target="builder-v2-display-button-label-input"
          />
        </div>
      )}
      renderTrigger={(ref, getReferenceProps) => {
        const props = getReferenceProps();
        const onClick = props.onClick as () => void;
        return <div ref={ref}>{children({ onOpen: onClick })}</div>;
      }}
    />
  );
}

export default observer(DisplayTypeDropdown);
